import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { listEmailSends, getEmailSend, EmailSend, EmailQuota } from '../api/client';
import EmailNav from '../components/EmailNav';
import './EmailHistory.css';
import { overlayClose } from '../utils/overlayClose';

/**
 * Email Send History, list view with stats summary and per-send detail modal.
 *
 * Each send is a card showing subject, recipient count, status pills, and
 * a friendly date. Click a card → modal with full recipient list and
 * the rendered body.
 */
export default function EmailHistory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const justSent = searchParams.get('just-sent');

  const [sends, setSends] = useState<EmailSend[]>([]);
  const [quota, setQuota] = useState<EmailQuota | null>(null);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openSendId, setOpenSendId] = useState<string | null>(null);
  const [openSendDetail, setOpenSendDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    listEmailSends()
      .then(data => {
        setSends(data.sends);
        setQuota(data.quota);
        setAnalyticsEnabled(data.emailAnalyticsEnabled === true);
      })
      .catch(err => {
        if (err instanceof Error && err.message.includes('not available')) {
          navigate('/dashboard');
          return;
        }
        setError(err instanceof Error ? err.message : 'Failed to load history');
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const [recipientEngagement, setRecipientEngagement] = useState<Record<string, any> | null>(null);

  const openDetail = (sendId: string) => {
    setOpenSendId(sendId);
    setOpenSendDetail(null);
    setRecipientEngagement(null);
    setDetailLoading(true);
    getEmailSend(sendId)
      .then(data => {
        setOpenSendDetail(data.send);
        setRecipientEngagement(data.recipientEngagement || null);
      })
      .catch(err => setError(err instanceof Error ? err.message : 'Failed to load send detail'))
      .finally(() => setDetailLoading(false));
  };

  const closeDetail = () => {
    setOpenSendId(null);
    setOpenSendDetail(null);
  };

  // Stats summary across visible sends
  const totalSends = sends.length;
  const totalRecipients = sends.reduce((acc, s) => acc + (s.recipientCount || 0), 0);
  const totalDelivered = sends.reduce((acc, s) => acc + (s.stats?.delivered || 0), 0);
  const totalBounced = sends.reduce((acc, s) => acc + (s.stats?.bounced || 0), 0);

  const quotaPct = quota?.monthlySendLimit
    ? Math.min(100, Math.round((quota.usedThisMonth / quota.monthlySendLimit) * 100))
    : 0;

  return (
    <div className="email-page-shell">
      <EmailNav />

      <div className="page-head">
        <div>
          <h1>Send History</h1>
          <p className="subtitle">Your recent emails to club members.</p>
        </div>
        <div className="head-actions">
          <a href="/email/compose" className="ebtn ebtn-primary">+ New Email</a>
        </div>
      </div>

      {justSent && (
        <div className="just-sent-banner">
          <span className="just-sent-icon">✉️</span>
          <div>
            <strong>Email sent.</strong>
            <span> Recipients should see it in their inbox within a minute.</span>
          </div>
          <button
            className="just-sent-dismiss"
            onClick={() => {
              searchParams.delete('just-sent');
              setSearchParams(searchParams);
            }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Stats summary */}
      {!loading && quota && (
        <div className="history-stats">
          <div className="stat-card stat-quota">
            <div className="stat-label">This month</div>
            <div className="stat-value">
              {quota.usedThisMonth}{quota.monthlySendLimit ? ` / ${quota.monthlySendLimit}` : ''}
            </div>
            <div className="stat-sub">
              {quota.monthlySendLimit === null
                ? 'Unlimited (Pro)'
                : 'sends used'}
            </div>
            {quota.monthlySendLimit && (
              <div className="quota-bar">
                <div className="quota-fill" style={{ width: `${quotaPct}%` }} />
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-label">Total sends</div>
            <div className="stat-value">{totalSends}</div>
            <div className="stat-sub">across all time</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Recipients reached</div>
            <div className="stat-value">{totalRecipients}</div>
            <div className="stat-sub">{totalDelivered} delivered</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Bounces</div>
            <div className={`stat-value ${totalBounced > 0 ? 'stat-warn' : ''}`}>{totalBounced}</div>
            <div className="stat-sub">{totalBounced > 0 ? 'auto-suppressed' : 'none'}</div>
          </div>
        </div>
      )}

      {/* Send list */}
      {loading ? (
        <div className="loading">Loading…</div>
      ) : sends.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">✉️</div>
          <h3>No emails sent yet</h3>
          <p>Send your first branded email to your club's members.</p>
          <a href="/email/compose" className="btn btn-primary" style={{ marginTop: 16 }}>
            Compose Email
          </a>
        </div>
      ) : (
        <div className="history-list">
          {sends.map(s => {
            const bounced = s.stats?.bounced || 0;
            const delivered = s.stats?.delivered || 0;
            const total = s.recipientCount || 0;
            return (
              <button
                key={s.sendId}
                className="history-card"
                onClick={() => openDetail(s.sendId)}
              >
                <div className="history-card-main">
                  <div className="history-card-subject">{s.subject || '(no subject)'}</div>
                  <div className="history-card-meta">
                    <span className="meta-item">{formatRelativeDate(s.sentAt)}</span>
                    <span className="meta-sep">·</span>
                    <span className="meta-item">by {s.sentBy}</span>
                  </div>
                </div>
                <div className="history-card-stats">
                  <div className="stat-pill stat-pill-recipients">
                    <span className="stat-pill-num">{total}</span>
                    <span className="stat-pill-label">recipient{total === 1 ? '' : 's'}</span>
                  </div>
                  {delivered === total && bounced === 0 ? (
                    <span className="status-pill status-success">✓ Delivered</span>
                  ) : (
                    <>
                      {delivered > 0 && (
                        <span className="status-pill status-success">
                          {delivered}/{total} delivered
                        </span>
                      )}
                      {bounced > 0 && (
                        <span className="status-pill status-error">
                          {bounced} bounced
                        </span>
                      )}
                    </>
                  )}
                  {analyticsEnabled && (
                    <EngagementPills stats={s.stats} recipients={total} />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {openSendId && (
        <div className="modal-overlay" {...overlayClose(closeDetail)}>
          <div className="modal-content history-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Email detail</h2>
              <button onClick={closeDetail} className="modal-close" aria-label="Close">×</button>
            </div>
            {detailLoading ? (
              <div className="loading">Loading…</div>
            ) : openSendDetail ? (
              <div className="history-detail">
                <div className="detail-row">
                  <span className="detail-label">Subject</span>
                  <span className="detail-value">{openSendDetail.subject}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Sent</span>
                  <span className="detail-value">{formatFullDate(openSendDetail.sentAt)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Sent by</span>
                  <span className="detail-value">{openSendDetail.sentBy}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Reply-to</span>
                  <span className="detail-value">{openSendDetail.replyTo}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Recipients ({openSendDetail.recipientCount})</span>
                  <span className="detail-value">
                    <div className="recipient-list">
                      {(openSendDetail.recipientEmails || []).map((e: string) => {
                        const eng = recipientEngagement?.[e.toLowerCase().trim()];
                        const opened = !!eng?.opens;
                        const clicked = !!eng?.clicks;
                        const className = `recipient-chip${analyticsEnabled ? (clicked ? ' recipient-chip-clicked' : opened ? ' recipient-chip-opened' : ' recipient-chip-noopen') : ''}`;
                        const titleParts: string[] = [];
                        if (opened) titleParts.push(`Opened ${eng.opens.count}× · first ${formatRelativeDate(eng.opens.firstAt)}`);
                        if (clicked) titleParts.push(`Clicked ${eng.clicks.count}× · first ${formatRelativeDate(eng.clicks.firstAt)}`);
                        if (analyticsEnabled && !opened && !clicked) titleParts.push('No open or click recorded');
                        const icon = analyticsEnabled
                          ? (clicked ? '🖱' : opened ? '👁' : '·')
                          : '';
                        return (
                          <span key={e} className={className} title={titleParts.join(' · ')}>
                            {icon && <span className="recipient-chip-icon">{icon}</span>}
                            {e}
                          </span>
                        );
                      })}
                    </div>
                    {analyticsEnabled && (
                      <div className="recipient-legend">
                        <span><span className="recipient-chip-icon">👁</span> opened</span>
                        <span><span className="recipient-chip-icon">🖱</span> clicked</span>
                        <span><span className="recipient-chip-icon">·</span> no engagement recorded</span>
                      </div>
                    )}
                  </span>
                </div>
                {openSendDetail.failedAddresses?.length > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Failed ({openSendDetail.failedAddresses.length})</span>
                    <span className="detail-value">
                      <div className="recipient-list">
                        {openSendDetail.failedAddresses.map((e: string) => (
                          <span key={e} className="recipient-chip recipient-chip-failed">{e}</span>
                        ))}
                      </div>
                    </span>
                  </div>
                )}
                {analyticsEnabled && (
                  <EngagementStatsCard
                    stats={openSendDetail.stats}
                    recipients={openSendDetail.recipientCount || 0}
                  />
                )}
                <div className="detail-row detail-row-stacked">
                  <span className="detail-label">Body (as sent)</span>
                  <iframe
                    className="detail-body-iframe"
                    srcDoc={openSendDetail.bodyHtml || '<p>(empty)</p>'}
                    title="Email body"
                  />
                </div>
              </div>
            ) : (
              <div className="error-message">Couldn't load send detail.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Friendly date formatting ─────────────────────────────── */

function formatRelativeDate(iso: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24 && now.getDate() === d.getDate()) {
    return `Today at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (diffDay === 1 || (diffHr < 48 && now.getDate() - d.getDate() === 1)) {
    return `Yesterday at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
  if (diffDay < 7) {
    return `${d.toLocaleDateString('en-GB', { weekday: 'long' })} at ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: now.getFullYear() === d.getFullYear() ? undefined : 'numeric' });
}

function formatFullDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/* ── Phase 3a engagement components ───────────────────────────
   Rendered only when the club has features.email_analytics = true.
   Read the lambda-side flag from the list/get response. */

interface EngagementStats {
  unique: number;
  total: number;
  lastEventAt?: string;
}

interface StatsBlob {
  opens?: EngagementStats;
  clicks?: EngagementStats;
  topLinks?: Record<string, number>;
}

/** Compact pills on each row in the send-history list. */
function EngagementPills({ stats, recipients }: { stats?: StatsBlob; recipients: number }) {
  if (!stats) return null;
  const openRate = ratePercent(stats.opens?.unique, recipients);
  const clickRate = ratePercent(stats.clicks?.unique, recipients);

  return (
    <>
      <span className={`status-pill ${badgeClass(openRate)}`} title={tooltipFor('opens', stats.opens, recipients)}>
        {pillText('Opens', stats.opens?.unique, recipients, openRate)}
      </span>
      <span className={`status-pill ${badgeClass(clickRate)}`} title={tooltipFor('clicks', stats.clicks, recipients)}>
        {pillText('Clicks', stats.clicks?.unique, recipients, clickRate)}
      </span>
    </>
  );
}

/** Detail-modal stats card with totals + top links. */
function EngagementStatsCard({ stats, recipients }: { stats?: StatsBlob; recipients: number }) {
  if (!stats) return null;
  const opens = stats.opens || { unique: 0, total: 0 };
  const clicks = stats.clicks || { unique: 0, total: 0 };
  const openRate = ratePercent(opens.unique, recipients);
  const clickRate = ratePercent(clicks.unique, recipients);
  const ctr = opens.unique > 0 ? Math.round((clicks.unique / opens.unique) * 100) : 0;

  const topLinks = Object.entries(stats.topLinks || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="detail-row detail-row-stacked engagement-card">
      <span className="detail-label">Engagement</span>
      <div className="engagement-grid">
        <div className="engagement-metric">
          <div className="engagement-metric-value">
            {opens.unique}<span className="engagement-metric-denom">/{recipients}</span>
          </div>
          <div className="engagement-metric-label">
            Opens <span className={`engagement-rate ${badgeClass(openRate)}`}>{openRate}%</span>
          </div>
          <div className="engagement-metric-sub">
            {opens.total} total opens
            {opens.lastEventAt ? ` · last ${formatRelativeDate(opens.lastEventAt)}` : ''}
          </div>
        </div>
        <div className="engagement-metric">
          <div className="engagement-metric-value">
            {clicks.unique}<span className="engagement-metric-denom">/{recipients}</span>
          </div>
          <div className="engagement-metric-label">
            Clicks <span className={`engagement-rate ${badgeClass(clickRate)}`}>{clickRate}%</span>
          </div>
          <div className="engagement-metric-sub">
            {clicks.total} total clicks
            {opens.unique > 0 ? ` · ${ctr}% of openers clicked` : ''}
          </div>
        </div>
      </div>
      {topLinks.length > 0 && (
        <div className="engagement-toplinks">
          <div className="engagement-toplinks-label">Top clicked links</div>
          <ul className="engagement-toplinks-list">
            {topLinks.map(([url, count]) => (
              <li key={url} className="engagement-toplink">
                <span className="engagement-toplink-count">{count}</span>
                <a href={url} target="_blank" rel="noopener noreferrer" className="engagement-toplink-url">
                  {url}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="engagement-disclaimer">
        ⓘ Open counts include privacy-protected clients (e.g. Apple Mail prefetch),
        which can inflate the number above actual human opens.
      </div>
    </div>
  );
}

function ratePercent(unique: number | undefined, denom: number): number {
  if (!unique || !denom) return 0;
  return Math.round((unique / denom) * 100);
}

function badgeClass(rate: number): string {
  if (rate >= 30) return 'status-success';
  if (rate >= 10) return 'status-warn';
  return 'status-neutral';
}

function pillText(label: string, unique: number | undefined, total: number, rate: number): string {
  const u = unique ?? 0;
  if (u === 0) return `0 ${label.toLowerCase()}`;
  return `${u}/${total} ${label.toLowerCase()} (${rate}%)`;
}

function tooltipFor(kind: 'opens' | 'clicks', s: EngagementStats | undefined, recipients: number): string {
  if (!s) return `No ${kind} recorded yet`;
  const lastSeen = s.lastEventAt ? ` Last ${kind.slice(0, -1)} ${formatRelativeDate(s.lastEventAt)}.` : '';
  return `${s.unique} unique ${kind} from ${recipients} recipients (${s.total} total events).${lastSeen}`;
}
